import {
  getRelativePosition,
  useGetArtworkById,
  useUpdateArtwork,
} from "@/api/artwork/artwork";
import {
  appendNoDuplicateRecordingNumbersRefine,
  artworkSchema,
} from "@/api/artwork/artwork-validation";
import { useGetAttachmentsByDocId } from "@/api/attachment";
import { useGetAllAuthors } from "@/api/author/author";
import { getCollectors } from "@/api/collector/collector";
import { useGetPermanentEnsembles } from "@/api/permanent-ensemble";
import ArtworkPrintSingleDrawer from "@/components/artwork/ArtworkPrintSingleDrawer";
import CreateArtworkSet from "@/components/ensemble/LinkSetsAndArtworks";
import * as AF from "@/components/form-items/AbstractForm";
import {
  FourDimensionalInput,
  ThreeDimensionalInput,
} from "@/components/form-items/DimensionsInput";
import { useMultilingualEmpty } from "@/components/form-items/hooks/useMultilingualEmpty";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import {
  DoubleMultilingualField,
  MultilingualField,
} from "@/components/form-items/MultilingualField";
import { AbstractFormSelectFetcher } from "@/components/form-items/select-fetcher-form";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/auth/AuthContext";
import { DashboardParamsProviders } from "@/context/DashboardParamsProviders";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import i18n, {
  getMainLanguage,
  isSupportedLocale,
  SupportedLocale,
} from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { PHOTOGRAPHY_TYPE_VALUES } from "@/lib/compute-field/artwork-module-helpers";
import {
  computeFieldRelatedAuthorsArtwork,
  computeFieldSummaryArtwork,
  computeFieldSummaryPhotographieMasterArtwork,
} from "@/lib/computeFields";
import { Artwork } from "@/lib/types";
import { cn, generateImageName } from "@/lib/utils";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { AddAdministrativeDocumentsDrawer } from "./AddAdministrativeDocument";
import { AdministrativeDocumentList } from "./AdministrativeDocumentList";
import { ArtworkFinancialMonitoringTab } from "./ArtworkFinancialMonitoringTab";
import { ArtworkHistoryTable } from "./ArtworkHistoryTable";
import TirageList from "./tirages/TirageList";

const schema = appendNoDuplicateRecordingNumbersRefine(
  artworkSchema.passthrough(),
);

const EditArtworks = ({ artwork }: { artwork: Artwork }) => {
  const { instanceData } = useAuth();
  const mainLanguage = getMainLanguage();

  const defaultValues = useMemo(() => {
    return {
      ...artwork,
      permanentEnsemblesIds: artwork.relatedEnsemble.reduce(
        (acc, ens) =>
          ens.ensemblesDescriptionType === "fixe" ? [...acc, ens.id] : acc,
        [] as string[],
      ),
      relatedAuthorIds: artwork.relatedAuthor.map((author) => author.id),
    };
  }, [artwork]);

  const form = useForm<FieldValues>({
    reValidateMode: "onSubmit",
    defaultValues,
    resolver: standardSchemaResolver(schema),
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const shouldDisplayVolume =
    form.watch("oeuvresdescriptiondimensionsprofondeur") || false;

  const isTirageKnownDescriptionEmpty = useMultilingualEmpty(
    "oeuvresTirageKnownDescription",
    form,
    "oeuvresTirageKnownDescriptionPublished",
  );

  const isTirageMasterDescriptionEmpty = useMultilingualEmpty(
    "oeuvresTirageMasterDescription",
    form,
    "oeuvresTirageMasterDescriptionPublished",
  );

  const { data: permanentEnsembles } = useGetPermanentEnsembles();
  // TODO: apply MultiSelect search
  const { data: authors } = useGetAllAuthors({
    where: {},
    order: { artistesAuteursDescriptionNom: "asc" },
    take: 50,
    skip: 0,
  });

  const { data: attachments = [] } = useGetAttachmentsByDocId(artwork.id);

  const { relativePosition } = useDashboardParams<Artwork>({
    statusField: "oeuvresPublicationStatus",
    resourceName: "artwork",
    relativePosition: {
      currentId: artwork.id,
      positionFetcher: getRelativePosition,
    },
  });

  const artworkUpdater = useUpdateArtwork();

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const [temporarySetModalOpen, setTemporarySetModalOpen] = useState(false);
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;
  const [refreshFiles, setRefreshFiles] = useState(false);

  const sansTitreChecked = form.watch("oeuvresDescriptionTitreSansTitre");

  useEffect(() => {
    if (sansTitreChecked) {
      form.setValue("oeuvresDescriptionTitre", t("oeuvresDescriptionTitreSansTitre"));
    }
  }, [sansTitreChecked, form, t]);

  const handleResume = (data: any) => {
    // Map author ids to Author objects for the template
    const authorIds: string[] = data.relatedAuthorIds;
    const foundAuthors = authorIds
      .map((id) => authors?.find((author) => author.id === id))
      .filter((author) => author !== undefined);
    const dataWithAuthors = {
      ...data,
      relatedAuthor: foundAuthors,
    };
    if (isModulePhotoEnabled) {
      return computeFieldSummaryPhotographieMasterArtwork(dataWithAuthors, {
        language: getMainLanguage(),
        lineBreak: true,
      });
    }
    const summary = computeFieldSummaryArtwork(dataWithAuthors, {
      language: getMainLanguage(),
      lineBreak: true,
    });

    return summary;
  };

  /*
   * Start Here
   * Check if we need to display the "Tirages" tab
   */
  const oeuvresDescriptionTypeOeuvreMultilingual = form.watch(
    "oeuvresDescriptionTypeOeuvreMultilingual",
  );

  const artworkTypeInMainLanguage = isSupportedLocale(mainLanguage)
    ? oeuvresDescriptionTypeOeuvreMultilingual?.[mainLanguage]
    : undefined;

  const isModulePhotoEnabled = useMemo(() => {
    if (!instanceData?.isModulePhotoEnabled) {
      return false;
    }

    if (!artworkTypeInMainLanguage) {
      return false;
    }

    return PHOTOGRAPHY_TYPE_VALUES.includes(artworkTypeInMainLanguage);
  }, [instanceData?.isModulePhotoEnabled, artworkTypeInMainLanguage]);

  const isSecureModuleReady = useMemo(() => {
    // If we don't have instance data yet, we cannot reliably decide
    if (!instanceData) {
      return false;
    }

    // If the photo module is disabled at instance level,
    // we don't need the artwork type to be resolved
    if (!instanceData.isModulePhotoEnabled) {
      return true;
    }

    // When the module is enabled, wait until the main language type is known
    return artworkTypeInMainLanguage !== undefined;
  }, [instanceData, artworkTypeInMainLanguage]);
  /*
   * End Here
   */

  const tabs = useMemo(
    () =>
      [
        {
          value: "generalInformation",
          label: t("artworksGeneralInfo"),
        },
        { value: "description", label: t("artworksDescription") },
        { value: "dimensions-poids", label: t("multipleDimensionsPoids") },
        {
          value: "localisation-historique",
          label: t("artworksLocalisationHistorique"),
        },
        { value: "regie", label: t("artworksRegie") },
        {
          value: "rangement-recolement",
          label: t("artworksRangementRecolement"),
        },
        { value: "photo-copyright", label: t("artworksPhotosCrédits") },
        { value: "notes", label: t("notes") },
        { value: "sources", label: t("sources") },
        {
          value: "admin-docs",
          label: t("artworksValidationsDocumentsAdministratifs"),
        },
        { value: "files", label: t("fichiers") },
        { value: "suivi-financier", label: t("artworksSuiviFinancier") },
        { value: "links", label: t("linkedEntries") },
        {
          value: "tirages",
          label: t("tiragesTabLabel"),
          hide: !isModulePhotoEnabled,
          subtab: [
            {
              value: "general-description",
              label: t("tiragesGeneralInformation"),
            },
            {
              value: "tirages-list",
              label: t("tiragesListTabLabel"),
            },
          ],
        },
      ] as const,
    [t, isModulePhotoEnabled],
  );

  const [, setSearchParam] = useSearchParams();

  const title = artwork.oeuvresDescriptionDateDeCreation
    ? artwork.oeuvresDescriptionTitre +
      " - " +
      artwork.oeuvresDescriptionDateDeCreation
    : artwork.oeuvresDescriptionTitre;

  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values: FieldValues) => {
            artworkUpdater.mutate(
              {
                ...(values as Artwork),
                ...(status && { oeuvresPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("artworkSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid["oeuvresDescriptionNumeroEnregistrement"] != undefined)
              setSearchParam({ tab: "generalInformation" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({ queryKey: ["artworks"] });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: artwork.id }],
        });
        queryClient.invalidateQueries({ queryKey: ["artworksDashboard"] });
      }}
      title={title}
      reference={artwork.oeuvresDescriptionNumeroEnregistrement}
      subtitle={computeFieldRelatedAuthorsArtwork(artwork, {
        language: i18n.language as SupportedLocale,
      })}
      id={artwork.id}
      type="artwork"
      publicationStatus={artwork.oeuvresPublicationStatus}
      picturesData={attachments && { pictures: attachments, type: "artwork" }}
      linkOverview={`/edit/artwork/overview/${artwork.id}`}
      computeImageName={(data) =>
        generateImageName({
          primaryType: "artwork",
          artwork,
          translatedPosition: data?.position ? t(data?.position) : undefined,
        })
      }
      relativePosition={relativePosition}
      form={form}
      print={<ArtworkPrintSingleDrawer artwork={artwork} />}
      embedAttachmentOnSubmit={true}
      keyStatus="oeuvresStatus"
    >
      <AF.AbstractForm form={form} tabs={tabs} ready={isSecureModuleReady}>
        <AF.Tab value="generalInformation">
          <AF.Col>
            <AF.Text name="oeuvresDescriptionNumeroEnregistrement" />
            <AF.Text name="oeuvresDescriptionNumeroDInventaire" />
            <AF.Text name="oeuvresDescriptionReferenceCollectionActuelle" />
            <AF.Rating name="oeuvresValidationSystemeDeNotationDeLoeuvre" />
            <AF.Text name="oeuvresDescriptionTitre" />
            <AF.MultiSelectField
              name="permanentEnsemblesIds"
              options={
                permanentEnsembles?.map((ensemble) => ({
                  label: ensemble.ensemblesDescriptionNomEnsemble,
                  value: ensemble.id,
                })) ?? []
              }
              label={t("oeuvresDescriptionEnsemblesFixes")}
              placeholder={t("ensembleSelectEnsemble")}
            />
            <Label
              className={cn(
                "flex flex-col gap-1",
                "text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal",
              )}
            >
              {t("oeuvresValidationAjoutUnEnsembleTemporaire")}
              <Button
                onClick={() => setTemporarySetModalOpen(true)}
                type="button"
                variant="link"
                className="w-min p-0"
              >
                {t("ensembleSelectEnsembleTemp")}
              </Button>

              <CreateArtworkSet
                disabled={isReader}
                open={temporarySetModalOpen}
                onOpenChange={setTemporarySetModalOpen}
                selectedArtworksIds={temporarySetModalOpen ? [artwork.id] : []}
                resetSelectedArtworks={() => setTemporarySetModalOpen(false)}
              />
            </Label>
            <AF.Row>
              <AF.Checkbox
                name="oeuvresPhotosPhotoScanAFaire"
                visible={!isModulePhotoEnabled}
              />
              <AF.Checkbox
                name="oeuvresPhotosPhotoOkWeb"
                visible={!isModulePhotoEnabled}
              />
            </AF.Row>
            <AF.Checkbox
              name="oeuvresPhotosPhotoHdOk"
              visible={!isModulePhotoEnabled}
            />
            <AF.RichText
              name="oeuvresDescriptionResume"
              computeAction={handleResume}
            />
          </AF.Col>
          <AF.Col>
            <AF.Row>
              <AF.Checkbox name="oeuvresValidationDescriptionOk" />
              <AF.Checkbox name="oeuvresValidationHistoriqueCollectionsOk" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="oeuvresValidationExpositionsOk" />
              <AF.Checkbox name="oeuvresValidationVentesOk" />
            </AF.Row>
            <Separator className="" />
            <AF.Row>
              <AF.Checkbox name="oeuvresValidationOeuvreDocumentaire" />
              <AF.Checkbox name="oeuvresValidationOeuvreMajeure" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="oeuvresValidationOeuvreImportante" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="oeuvresValidationFaux" />
              <AF.Checkbox name="oeuvresValidationDestroy" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="oeuvresValidationAttributionDouteuse" />
              <AF.Checkbox name="oeuvresValidationLost" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="oeuvresNotPublished" />
              <AF.Checkbox name="oeuvresValidationStole" />
            </AF.Row>
            <AF.Checkbox name="oeuvresWaitingPublication" />
            <Separator />
            <ModifiableList
              listKey="oeuvresDescriptionAjoutDeMotsCles"
              name="oeuvresDescriptionAjoutDeMotsCles"
              append
            />
            <AF.Col>
              <Separator visible={isModulePhotoEnabled} />
              <AF.Row>
                <AF.Checkbox
                  name="oeuvresPhotosPhotoScanAFaire"
                  visible={isModulePhotoEnabled}
                />
                <AF.Checkbox
                  name="oeuvresPhotosPhotoOkWeb"
                  visible={isModulePhotoEnabled}
                />
              </AF.Row>
              <AF.Checkbox
                name="oeuvresPhotosPhotoHdOk"
                visible={isModulePhotoEnabled}
              />
              <Separator visible={isModulePhotoEnabled} />
            </AF.Col>
            <AF.RichText name="oeuvresValidationNotesDeTravail" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab className="flex-col" value="description">
          <AF.Row className="items-start">
            <AF.Col>
              <AF.MultiSelectField
                name="relatedAuthorIds"
                options={
                  authors?.map((author) => ({
                    label: `${author.artistesAuteursDescriptionPrenom} ${author.artistesAuteursDescriptionNom}`,
                    value: author.id,
                  })) ?? []
                }
                label={t("estampesMultiplesDescriptionAuteurArtiste")}
                placeholder={t("select")}
              />
              <AF.RadioGroup
                resettable={true}
                items={[
                  { value: "oeuvresDescriptionNomArtisteAttribueA" },
                  { value: "oeuvresDescriptionNomArtisteAnonyme" },
                  { value: "oeuvresDescriptionNomArtisteDApres" },
                  { value: "oeuvresDescriptionNomArtisteDansLeGoutDe" },
                  { value: "oeuvresDescriptionNomArtisteEntourageDe" },
                  { value: "oeuvresDescriptionNomArtisteAtelierDe" },
                ]}
              />
              <AF.Text name="oeuvresDescriptionTitre" />
              <AF.RadioGroup
                resettable={true}
                items={[
                  { value: "oeuvresDescriptionTitreSansTitre" },
                  { value: "oeuvresDescriptionTitreInconnu" },
                  { value: "oeuvresValidationTitleNotConfirmed" },
                ]}
              />
              <AF.Row>
                <AF.Checkbox
                  name="oeuvresDescriptionOeuvreDoubleFace"
                  align={true}
                  visible={!isModulePhotoEnabled}
                />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresDescriptionTechniqueConfirmer" />
                <AF.Checkbox name="oeuvresDescriptionSupportConfirmer" />
              </AF.Row>
              <ModifiableList
                name="oeuvresPhotoProjectDescription"
                listKey="liste-projects-photo"
                visible={isModulePhotoEnabled}
              />
              <ModifiableList
                name="oeuvresDescriptionLieuDeCreation"
                listKey="liste-lieux-creation"
                visible={isModulePhotoEnabled}
              />
            </AF.Col>
            <AF.Col>
              <AF.Row>
                <AF.Text name="oeuvresDescriptionDateDeCreation" />
                <AF.Checkbox
                  name="oeuvresDescriptionDateDeCreationInconnue"
                  align={true}
                />
              </AF.Row>
              <AF.Text name="oeuvresDescriptionSousTitre" />
              <AF.Text name="oeuvresDescriptionAutreTitre" />
              <AF.Row className="[&>*]:flex-1">
                <AF.Text name="oeuvresDescriptionReference" />
                <ModifiableList
                  name="oeuvresDescriptionCategorie"
                  listKey="liste-categories"
                />
              </AF.Row>
              <AF.Row>
                <ModifiableList
                  name="oeuvresDescriptionTheme"
                  listKey="liste-themes"
                />
                <ModifiableList
                  name="oeuvresDescriptionSerie"
                  listKey="liste-series"
                />
              </AF.Row>
              <ModifiableList
                name="oeuvresPhotoPeopleAndModelNames"
                listKey="liste-people-and-models"
                visible={isModulePhotoEnabled}
              />
            </AF.Col>
          </AF.Row>
          <Separator />
          <AF.Col>
            <span>{t("forms.multilingual-fields.indication")}</span>
            <AF.Row className="items-start">
              <DoubleMultilingualField
                className="w-full"
                render={(lang) => (
                  <AF.Col>
                    <ModifiableList
                      name={`oeuvresDescriptionTypeOeuvreMultilingual.${lang}`}
                      label={t("oeuvresDescriptionTypeOeuvreMultilingual")}
                      listKey={`liste-types-oeuvres`}
                      lang={lang}
                    />
                    <ModifiableList
                      name={`oeuvresDescriptionTechnique.${lang}`}
                      label={t("oeuvresDescriptionTechnique")}
                      listKey={`liste-techniques`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("oeuvresDescriptionSupport")}
                      name={`oeuvresDescriptionSupport.${lang}`}
                      listKey={`liste-supports`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("oeuvresDescriptionRemarqueSupport")}
                      name={`oeuvresDescriptionRemarqueSupport.${lang}`}
                      listKey={`liste-remarques-support`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("oeuvresDescriptionSignatureLocalisation")}
                      name={`oeuvresDescriptionSignatureLocalisation.${lang}`}
                      listKey={`liste-signatures`}
                      lang={lang}
                    />
                    <AF.Text
                      label={t("oeuvesDescriptionSignatureTranscription")}
                      name="oeuvresDescriptionSignatureTranscription"
                    />
                    <AF.Text
                      label={t(
                        "artwork.form.oeuvresDescriptionInscriptionsDos",
                      )}
                      name={`oeuvresDescriptionInscriptionsDosMultilingual.${lang}`}
                      corner={() => (
                        <AF.Switch
                          name={`oeuvresDescriptionInscriptionsDosPublished.${lang}`}
                        />
                      )}
                    />
                  </AF.Col>
                )}
              />
            </AF.Row>
          </AF.Col>
          <Separator />
          <AF.Row className="items-start">
            <AF.Col>
              <ModifiableList
                name="oeuvresDescriptionRealisationFabrication"
                listKey="liste-fabricants-imprimeurs"
                visible={!isModulePhotoEnabled}
              />
              <AF.Row>
                <ModifiableList
                  name="oeuvresDescriptionPeriodeCreation"
                  listKey="liste-periodes-creation"
                />
                <ModifiableList
                  name="oeuvresDescriptionAireGeographique"
                  listKey="liste-aires-geographiques"
                />
              </AF.Row>
              <ModifiableList
                name="oeuvresDescriptionEcoleMouvement"
                listKey="liste-ecoles-mouvements"
              />
              <ModifiableList
                name="oeuvresDescriptionLieuDeCreation"
                listKey="liste-lieux-creation"
                visible={!isModulePhotoEnabled}
              />
            </AF.Col>
            <AF.Col>
              <AF.RichText name="oeuvresDescriptionDescriptionLongue" />
              <AF.Text name="oeuvresDescriptionNomFichierNumerique" />
              <AF.Checkbox name="oeuvresDescriptionRestaurationNumerique" />
            </AF.Col>
          </AF.Row>
        </AF.Tab>
        <AF.Tab value="dimensions-poids">
          <AF.Col>
            <FourDimensionalInput
              parentName="oeuvresDescriptionDimensionsOeuvre"
              needVolume={shouldDisplayVolume}
              needSurface={!shouldDisplayVolume}
            />
            <AF.RadioGroup
              items={[
                {
                  value: "oeuvresDescriptionDimensionsOeuvreVerifiees",
                },
                {
                  value: "oeuvresDescriptionDimensionsOeuvreInconnues",
                },
                {
                  value: "oeuvresDescriptionDimensionsOeuvreDouteuses",
                },
              ]}
            />
            <AF.Checkbox name="oeuvresdescriptiondimensionsprofondeur" />
            <AF.Text
              className="w-20"
              name="oeuvresDescriptionPoidsOeuvre"
              unit="kg"
            />
            <AF.Text
              name="oeuvresDescriptionDimensionsOeuvreRaw"
              label={t("oeuvresDescriptionDimensionsOeuvreRaw")}
              sublabel={t("oeuvresDescriptionDimensionsOeuvreRawSublabel")}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="localisation-historique">
          <AF.Col>
            <AbstractFormSelectFetcher
              label={t("oeuvresLocalisationHistoriqueCollection")}
              name="oeuvresLocalisationHistoriqueCollectionId"
              fetcher={async () => await getCollectors({})}
              queryKey={["collectors"]}
              allowClear={true}
              getContent={(collector) => (
                <>
                  {collector.collectionneursDescriptionNom}{" "}
                  {collector.collectionneursDescriptionPrenom}{" "}
                  {collector.collectionneursDescriptionVille
                    ? `(${collector.collectionneursDescriptionVille})`
                    : ""}
                </>
              )}
              getLabel={(collector) =>
                `${collector.collectionneursDescriptionNom} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionVille ? `(${collector.collectionneursDescriptionVille})` : ""}`
              }
              getValue="id"
            />
            <AF.Checkbox name="oeuvresLocalisationHistoriqueOeuvreNonLocalisee" />
            <Separator />
            <ModifiableList
              name="oeuvresLocalisationHistoriqueCollectionCatalogue"
              listKey="liste-collections-catalogue"
            />
            <Separator />
            <AF.RichText name="oeuvresLocalisationHistoriqueCollectionNote" />
            <Separator />
            <ArtworkHistoryTable artworkId={artwork.id} />
            <Separator />
            <AF.RichText name="oeuvresLocalisationHistoriqueNotes" />
            <Separator />
            <AF.Checkbox name="oeuvresLocalisationHistoriqueDepotAffectionPret" />
            <AF.Row>
              <AF.Text name="oeuvresLocalisationHistoriqueDateDepotAffectionPret" />
              <AF.Text name="oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret" />
            </AF.Row>
            <AF.RichText name="oeuvresLocalisationHistoriqueNoteDepotAffectionPret" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="regie">
          <AF.Col>
            <AF.Checkbox name="oeuvresRegieCadre" />
            <Separator />
            <AF.RichText name="oeuvresRegieDescriptionCadre" />
            <Separator />
            <ThreeDimensionalInput
              parentName="oeuvresRegieDimensionsCadre"
              volumeAutomaticToggle={true}
            />
            <Separator />
            <AF.RichText name="oeuvresRegieNotesRegie" />
          </AF.Col>
          <AF.Col>
            <AF.Checkbox name="oeuvresRegieCaisseTransport" />
            <Separator />
            <AF.RichText name="oeuvresRegieDescriptionCaisse" />
            <Separator />
            <ThreeDimensionalInput
              parentName="oeuvresRegieDimensionsCaisse"
              volumeAutomaticToggle={true}
            />
            <Separator />
            <AF.Row>
              <AF.Text
                name="oeuvresRegiePoidsCaisseAvecOeuvre"
                unit="kg"
                className="w-1/2"
              />
              <AF.Text
                name="oeuvresRegiePoidsCaisse"
                unit="kg"
                className="w-1/2"
              />
            </AF.Row>
            <Separator />
            <AF.RichText name="oeuvresRegieNotesCondition" />
            <AF.Checkbox name="oeuvresRestored" />
            <AF.Checkbox name="oeuvresToRestore" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="rangement-recolement">
          <AF.Col>
            <ModifiableList
              name="oeuvresLocalisationHistoriqueLieuDeStockage"
              listKey="liste-lieux-stockage"
            />
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementPieceBox" />
              <AF.Text name="oeuvresRangementRecolementRangee" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementRack" />
              <AF.Text name="oeuvresRangementRecolementMeuble" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementEtagere" />
              <AF.Text name="oeuvresRangementRecolementBoiteCarton" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementClasseur" />
              <AF.Text name="oeuvresRangementRecolementAutreReferenceRangement" />
            </AF.Row>
            <AF.Text
              name="oeuvresRangementRecolementDateRangement"
              className="w-[49%]"
            />
            <AF.RichText name="oeuvresLocalisationHistoriqueNotesRangement" />
          </AF.Col>
          <AF.Col>
            <AF.Checkbox name="oeuvresRangementRecolementMarquage" />
            <AF.Text
              name="oeuvresRangementRecolementDateMarquage"
              className="w-[49%]"
            />
            <Separator />
            <AF.Text
              name="oeuvresRangementRecolementOperateurRecolement"
              className="w-[49%]"
            />
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementDateRecolement" />
              <AF.Text name="oeuvresRangementRecolementReferenceCampagneRecolement" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementAutreReferenceRecolement" />
              <AF.Checkbox
                name="oeuvresRangementRecolementRecolementOk"
                align={true}
              />
            </AF.Row>
            <AF.RichText name="oeuvresRangementRecolementNotesRecolement" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="photo-copyright">
          <AF.Col>
            <AF.RichText name="oeuvresPhotosNotesPhotos" />
            <AF.RichText name="oeuvresPhotosNotesPreparationPhotos" />
            <ModifiableList
              name="oeuvresCreditsCreditOeuvre"
              listKey="liste-credits"
            />
            <ModifiableList
              name="oeuvresCreditsCreditPhotoOeuvre"
              listKey="liste-credits"
            />
            <AF.Row>
              <ModifiableList
                name="oeuvresCreditsNatureDroits"
                listKey="liste-credits"
              />
              <AF.LegacySelectCurrency name="oeuvresCreditsMontantDroits" />
            </AF.Row>
            <AF.RichText name="oeuvresCreditsRemarqueCreditsDroits" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="notes">
          <AF.Col>
            <AF.RichText name="oeuvresValidationNotesDeTravail" />
            <div className="flex w-full gap-4">
              <DoubleMultilingualField
                render={(lang) => (
                  <AF.RichText
                    name={`oeuvresValidationRemarquesPubliquesMultilingual.${lang}`}
                    label={t(
                      "artwork.form.oeuvresValidationRemarquesPubliques",
                    )}
                  />
                )}
              />
            </div>
            <AF.RichText name="oeuvresValidationRemarquesPersonnelles" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.Col>
            <AF.RichText name="sources" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab
          value="admin-docs"
          headerExtras={
            <AddAdministrativeDocumentsDrawer
              disabled={isReader}
              artwork={artwork}
              artworkAttachments={attachments}
            />
          }
        >
          <AF.Col>
            <AdministrativeDocumentList artworkId={artwork.id} />
            <Separator />
            <AF.Checkbox name="oeuvresAdminDocLettreInclusionEdit" />
            <Separator />
            <AF.Checkbox name="oeuvresAdminDocCertificatExist" />
            <Separator />
            <AF.RichText name="oeuvresAdministratifNotes" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={
            <AddFileModal
              id={artwork.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={artwork.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={(refresh: boolean) => {
              setRefreshFiles(refresh);
            }}
          />
        </AF.Tab>
        <AF.Tab value="suivi-financier" isForm={false}>
          <ArtworkFinancialMonitoringTab relatedArtworkId={artwork.id} />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={artwork.id}
            type="artwork"
            data={artwork}
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                    {
                      name: "artworkInExhibitions",
                      label: t("entryexhibitions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t("exhibitionDescriptionNumeroExpo"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t("exhibitionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          label: t(
                            "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansExpositionsNotes",
                          label: t("oeuvresDansExpositionsNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "artworkInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                          label: t(
                            "bibliographieDescriptionNumeroBibliographie",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliography.bibliographieDescriptionResumeLong",
                          label: t("bibliographieDescriptionResumeLong"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionReproduitPage",
                          label: t(
                            "oeuvresDansBibliographieDescriptionReproduitPage",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionCitePage",
                          label: t(
                            "oeuvresDansBibliographieDescriptionCitePage",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionPhotographe",
                          label: t(
                            "oeuvresDansBibliographieDescriptionPhotographe",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionNonPagine",
                          label: t(
                            "oeuvresDansBibliographieDescriptionNonPagine",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieDescriptionReproductionCouleur",
                          label: t(
                            "oeuvresDansBibliographieDescriptionReproductionCouleur",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansBibliographieNotes",
                          label: t("oeuvresDansBibliographieNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "artworkInAuctions",
                      label: t("entryauctions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNumeroLot",
                          label: t("oeuvresDansVentesDescriptionNumeroLot"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionReproduitPage",
                          label: t("oeuvresDansVentesDescriptionReproduitPage"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationBasse",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationBasse",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationHaute",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationHaute",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionVendu",
                          label: t("oeuvresDansVentesDescriptionVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionResultatNonListe",
                          label: t(
                            "oeuvresDansVentesDescriptionResultatNonListe",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVenu",
                          label: t("oeuvresDansVentesDescriptionNonVenu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVendu",
                          label: t("oeuvresDansVentesDescriptionNonVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonReproduit",
                          label: t("oeuvresDansVentesDescriptionNonReproduit"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionRetire",
                          label: t("oeuvresDansVentesDescriptionRetire"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionMiseAPrix",
                          label: t("oeuvresDansVentesDescriptionMiseAPrix"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixMarteau",
                          label: t("oeuvresDansVentesDescriptionPrixMarteau"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixAvecFrais",
                          label: t("oeuvresDansVentesDescriptionPrixAvecFrais"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNotes",
                          label: t("oeuvresDansVentesDescriptionNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
        <AF.Tab value="tirages-general-description">
          <AF.Col>
            <MultilingualField
              corner={(_) => (
                <AF.Switch
                  name={`oeuvresTirageKnownDescriptionPublished`}
                  switchProps={{ disabled: isTirageKnownDescriptionEmpty }}
                />
              )}
              render={(lang) => (
                <AF.RichText
                  name={`oeuvresTirageKnownDescription.${lang}`}
                  label={t("artwork.form.oeuvresTirageKnownDescription")}
                />
              )}
            />
            <MultilingualField
              corner={(_) => (
                <AF.Switch
                  name={`oeuvresTirageMasterDescriptionPublished`}
                  switchProps={{ disabled: isTirageMasterDescriptionEmpty }}
                />
              )}
              render={(lang) => (
                <AF.RichText
                  name={`oeuvresTirageMasterDescription.${lang}`}
                  label={t("artwork.form.oeuvresTirageMasterDescription")}
                />
              )}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="tirages-tirages-list" showHeader={false}>
          <DashboardParamsProviders
            storageKey="tirage"
            persistInSearchParams={false}
          >
            <TirageList artworkId={artwork.id} />
          </DashboardParamsProviders>
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditArtworksPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const { data, isLoading } = useGetArtworkById(id, false);

  return (
    <>
      {data && <EditArtworks key={data.id} artwork={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">{t("artworkNexistePasLabel")}</h2>
      )}
    </>
  );
};
